import { useEffect, useState } from "react";
import { Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";

import { crmService } from "@/services/crmService";
import { ContactForm, emptyContact, contactPayload } from "@/components/crm/ContactForm";
import { parseFieldErrors } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export const EditContactDialog = ({ open, onOpenChange, contact, onSaved }) => {
  const [form, setForm] = useState(emptyContact);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (contact) {
      setForm({
        ...emptyContact,
        ...Object.fromEntries(Object.keys(emptyContact).map((k) => [k, contact[k] ?? emptyContact[k]])),
        tag_ids: (contact.tags || []).map((t) => t.id),
        lead_source_id: contact.lead_source?.id || "",
      });
      setErrors({});
    }
  }, [contact]);

  const save = async () => {
    setErrors({});
    setSaving(true);
    try {
      const updated = await crmService.updateContact(contact.id, contactPayload(form));
      toast.success("Contact updated");
      onSaved?.(updated);
      onOpenChange(false);
    } catch (err) {
      setErrors(parseFieldErrors(err));
      toast.error(err.message || "Please correct the highlighted fields");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="edit-contact-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Pencil className="h-5 w-5 text-accent" /> Edit Contact</DialogTitle>
          <DialogDescription>Update this contact's details.</DialogDescription>
        </DialogHeader>
        <ContactForm form={form} set={set} errors={errors} idPrefix="edit" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="edit-contact-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="edit-contact-save">
            {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
